import type { CentinelaCase } from "../../../features/centinela/types";
import { toWatchStatus } from "./mapCentinelaUnit";
import type { WatchStatus } from "../types";

export const CASE_LADDER = ["detectado", "llamando", "escalado"] as const;

export type CaseLadderStep = (typeof CASE_LADDER)[number] | "resuelto";

export const CASE_URGENCIES = ["critica", "alta", "media", "baja"] as const;

export type CaseUrgency = (typeof CASE_URGENCIES)[number];

export const CASE_URGENCY_WEIGHT = {
  critica: 0,
  alta: 1,
  media: 2,
  baja: 3,
} as const satisfies Record<CaseUrgency, number>;

const isUrgency = (value: string): value is CaseUrgency =>
  (CASE_URGENCIES as readonly string[]).includes(value);

export const toCaseUrgency = (value: string | null | undefined): CaseUrgency =>
  value && isUrgency(value) ? value : "baja";

export const CASE_ORIGINS = ["alerta", "manual", "rutina"] as const;

export type CaseOrigin = (typeof CASE_ORIGINS)[number];

const isNonAlertKind = (kind: string): kind is Exclude<CaseOrigin, "alerta"> =>
  kind === "manual" || kind === "rutina";

export const toCaseOrigin = (kind: string): CaseOrigin =>
  isNonAlertKind(kind) ? kind : "alerta";

export interface CentinelaCaseView {
  id: string;
  deviceId: string;
  unitName: string | null;
  tripId: string | null;
  kind: string;
  origin: CaseOrigin;
  threatId: string | null;
  status: string;
  urgency: CaseUrgency;
  isOpen: boolean;
  step: CaseLadderStep;
  attempts: number;
  callsCount: number;
  requiresHuman: boolean;
  theftScore: number;
  summary: string | null;
  /** El hecho con números; cae al nombre del tipo en casos viejos. */
  hecho: string | null;
  donde: string | null;
  duracionMin: number | null;
  porQue: string | null;
  resolution: string | null;
  openedAt: string;
  closedAt: string | null;
  unitStatus: WatchStatus | null;
}

export function caseLadderStep(item: CentinelaCase): CaseLadderStep {
  if (item.status === "resuelto" || item.closed_at) return "resuelto";
  if (item.status === "escalado") return "escalado";
  if ((item.driver_contact_attempts ?? 0) > 0 || (item.calls_count ?? 0) > 0) return "llamando";
  return "detectado";
}

export function mapCentinelaCase(item: CentinelaCase): CentinelaCaseView {
  return {
    id: item.id,
    deviceId: item.device_id,
    unitName: item.device_name ?? item.vehicle_name ?? null,
    tripId: item.trip_id ?? null,
    kind: item.kind,
    origin: toCaseOrigin(item.kind),
    threatId: item.alert_type_id ?? (isNonAlertKind(item.kind) ? null : item.kind),
    status: item.status,
    urgency: toCaseUrgency(item.urgency),
    isOpen: item.status !== "resuelto" && !item.closed_at,
    step: caseLadderStep(item),
    attempts: item.driver_contact_attempts ?? 0,
    callsCount: item.calls_count ?? 0,
    requiresHuman: item.requires_human === true,
    theftScore: Number.isFinite(item.theft_score) ? item.theft_score : 0,
    hecho: item.alert_label ?? item.kind_label ?? null,
    donde: item.donde ?? null,
    duracionMin: item.duracion_min ?? null,
    porQue: item.por_que ?? null,
    summary: item.ai_summary?.trim() || null,
    resolution: item.resolution?.trim() || null,
    openedAt: item.opened_at,
    closedAt: item.closed_at ?? null,
    unitStatus: item.unit_status ? toWatchStatus(item.unit_status) : null,
  };
}

export function mapCentinelaCases(cases: CentinelaCase[] | undefined): CentinelaCaseView[] {
  return (cases ?? [])
    .map(mapCentinelaCase)
    .sort((a, b) => b.openedAt.localeCompare(a.openedAt));
}

export function worstOpenCaseByDevice(
  cases: CentinelaCaseView[],
): Map<string, CentinelaCaseView> {
  const worst = new Map<string, CentinelaCaseView>();

  for (const item of cases) {
    if (!item.isOpen) continue;
    const current = worst.get(item.deviceId);
    if (
      !current ||
      CASE_URGENCY_WEIGHT[item.urgency] < CASE_URGENCY_WEIGHT[current.urgency]
    ) {
      worst.set(item.deviceId, item);
    }
  }

  return worst;
}

export interface ThreatTally {
  open: number;
  total: number;
  deviceIds: Set<string>;
}

export function tallyThreats(cases: CentinelaCaseView[]): Map<string, ThreatTally> {
  const tally = new Map<string, ThreatTally>();

  for (const item of cases) {
    if (item.threatId === null) continue;

    const row = tally.get(item.threatId) ?? { open: 0, total: 0, deviceIds: new Set<string>() };
    row.total += 1;
    if (item.isOpen) {
      row.open += 1;
      row.deviceIds.add(item.deviceId);
    }
    tally.set(item.threatId, row);
  }

  return tally;
}
